<template>
  <v-card outlined :loading="isLoading || pageLoading">
    <v-card-subtitle class="grey lighten-4 pa-3">Languages</v-card-subtitle>
    <v-list>
      <page-language-item
        v-for="language in availableLanguages"
        :key="language.id"
        :language="language.language"
        :parent-page="parentPage"
        :site="language.site"
        :has-changes="hasChanges"
        :pages="pages"
        :large="large"
        :can-update="canUpdate"
        @refresh="$emit('refresh')"
      />
    </v-list>
    <v-card-actions class="grey lighten-4 pa-3">
      <template v-if="parentPage.language.code !== 'en'">
        <v-icon small left>info</v-icon>
        <span class="grey--text text--darken-1" style="font-size: 12px"
          >Translations can <b>only</b> be performed from English pages</span
        >
      </template>
      <v-tooltip left>
        <template #activator="{ on }">
          <div v-show="showTranslateButton" class="ml-auto" v-on="!canTranslateAll && on">
            <v-btn
              depressed
              small
              color="secondary"
              :loading="isLoading"
              :disabled="isLoading || !canTranslateAll"
              @click="showTranslationModal = true"
            >
              Translate all
            </v-btn>
          </div>
        </template>
        <span>Cannot translate until the following actions are performed:</span>
        <ul>
          <li v-if="!canUpdate">User must have update permissions</li>
          <li v-if="hasChanges">All changes must be saved or reset</li>
          <li v-if="parentPage.wp_publish_status !== 'publish'">Status must be set to Published</li>
          <li v-if="!parentPage.wp_post_id">Post ID must be set</li>
          <li v-if="parentPage.is_revised">Page must not contain revised changes</li>
        </ul>
      </v-tooltip>
    </v-card-actions>

    <translate-modal
      v-model="showTranslationModal"
      :parent-page="parentPage"
      :translating-pages="translatablePages"
      :is-disabled="isLoading"
      @modal-confirm="onTranslateConfirm"
    />
  </v-card>
</template>

<script>
// Services
import { pages } from '@/services/offers'
// Components
import PageLanguageItem from './page-language-item'
import TranslateModal from '@/views/content/pages/components/translate-modal'

export default {
  name: 'PageLanguageList',
  components: { PageLanguageItem, TranslateModal },
  props: {
    availableLanguages: {
      type: Array,
      required: true,
    },
    hasChanges: {
      type: Boolean,
      default: false,
    },
    parentPage: {
      type: Object,
      required: true,
    },
    pages: {
      type: Array,
      required: true,
    },
    large: {
      type: Boolean,
      default: false,
    },
    pageLoading: {
      type: Boolean,
      default: false,
    },
    canUpdate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      showTranslationModal: false,
    }
  },
  computed: {
    availablePages() {
      return this.availableLanguages.map(({ language: { id: languageId }, site: { id: siteId } }) =>
        this.pages.find(({ language_id, site_id }) => siteId === site_id && languageId == language_id)
      )
    },
    canTranslateAll() {
      if (!this.canUpdate) return false
      if (this.hasChanges) return false
      if (this.parentPage.language.code !== 'en') return false
      if (this.parentPage.wp_publish_status !== 'publish') return false
      if (!this.parentPage.wp_post_id) return false
      if (this.parentPage.is_revised) return false
      return true
    },
    showTranslateButton() {
      return this.parentPage.language.code === 'en'
    },
    translatablePages() {
      return this.availableLanguages.filter(({ language }) => language.code !== 'en')
    },
  },
  methods: {
    async onTranslateConfirm() {
      await this.translate()
      this.showTranslationModal = false
    },
    async translate() {
      this.isLoading = true

      try {
        const payload = this.translatablePages.map(({ language_id, site_id }) => ({ site_id, language_id }))
        await pages.translate(this.parentPage.id, payload)
        this.$notify({
          type: 'success',
          text: 'Successfully translated pages',
        })
        this.$emit('refresh')
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to translate pages.', data: { error }, duration: -1 })
      }
      this.isLoading = false
    },
  },
}
</script>
